import Emittery from 'emittery';

import { Flavor, trackError } from 'backend-client-web';

import DragManager, { DragHandler } from '../drag/DragManager';

import { LOGINS_DRAG_TYPE, LoginDragItem } from './login';

type DndTypeToItemType = {
  [LOGINS_DRAG_TYPE]: LoginDragItem;
};

export type DndType = keyof DndTypeToItemType;
export type DndItem<T extends DndType> = DndTypeToItemType[T];

interface DragSourceOptions<T extends DndType> {
  type: T;
  // Setting item to undefined indicates that the element is generally draggable, but can not be
  // dragged at the moment. This can for example be read-only logins. We still register them as drag
  // targets, but cancel drag triggered from them immediately. This avoid the user trying to drag
  // the item and accidentally starting a text selection, and mirrors the behavior of our previous
  // dnd library react-dnd.
  item?: DndItem<T>;
  el: HTMLElement;
  setPlaceholderPosition: (position: { x: number; y: number } | undefined) => void;
  getPlaceholderSize: () => { width: number; height: number } | undefined;
}

interface DropTargetOptions<T extends DndType> {
  accept: T;
  el: HTMLElement;
  canDrop: (item: DndItem<T>) => boolean;
  onDrop: (item: DndItem<T>) => void;
}

export interface DndDragEventPayload<T extends DndType> {
  id: DragSourceId;
  type: T;
  item: DndItem<T>;
  overTargetId: DropTargetId | undefined;
}

export type DragSourceId = Flavor<string, 'DragSourceId'>;
export type DropTargetId = Flavor<string, 'DropTargetId'>;

// Height of area at top and bottom of scrollable elements where dragging the drop item over will
// scroll them in the respective direction.
const SCROLL_AREA_HEIGHT_PX = 20;
const SCROLL_INTERVAL_MS = 10;
const SCROLL_PX = 3;

@Emittery.mixin('emitter')
class DndManager {
  private readonly dragSources = new Map<DragSourceId, DragSourceOptions<DndType>>();
  private readonly dropTargets = new Map<DropTargetId, DropTargetOptions<DndType>>();

  constructor(private dragManager: DragManager) {}

  public init() {
    this.dragManager.addHandler(this.dragHandler, 1);
    document.documentElement.addEventListener('keydown', this.handleKeydown, true);
  }

  public close() {
    this.dragManager.removeHandler(this.dragHandler);
    document.documentElement.removeEventListener('keydown', this.handleKeydown, true);
  }

  public addDragSource<T extends DndType>(id: DragSourceId, options: DragSourceOptions<T>) {
    this.dragSources.set(id, options);
  }

  public removeDragSource(id: DragSourceId) {
    this.dragSources.delete(id);
  }

  public addDropTarget<T extends DndType>(id: DropTargetId, options: DropTargetOptions<T>) {
    this.dropTargets.set(id, options as unknown as DropTargetOptions<DndType>);
  }

  public removeDropTarget(id: DropTargetId) {
    this.dropTargets.delete(id);
  }

  private dragHandler: DragHandler<{
    sourceId: DragSourceId;
    activeDnd?: {
      offsetX: number;
      offsetY: number;
      activeTargetId?: DropTargetId;
      scroll?: {
        el: Element;
        direction: 'up' | 'down';
        intervalId: number;
      };
    };
  }> = {
    isInDragArea: (e) => {
      const sourceId = this.getSourceIdForEvent(e);
      if (sourceId) {
        return {
          isInDragArea: true,
          dragPayload: { sourceId },
        };
      }
      return { isInDragArea: false };
    },
    onDragStart: (e, dragPayload) => {
      const sourceOptions = this.dragSources.get(dragPayload.sourceId);
      if (!sourceOptions || !sourceOptions.item) {
        return;
      }

      const sourceBoundingBox = sourceOptions.el.getBoundingClientRect();
      const offsetX = clamp(0, e.clientX - sourceBoundingBox.x, sourceBoundingBox.width);
      const offsetY = clamp(0, e.clientY - sourceBoundingBox.y, sourceBoundingBox.height);

      dragPayload.activeDnd = {
        offsetX,
        offsetY,
      };

      this.updatePlaceholderPosition(e, sourceOptions, dragPayload.activeDnd);

      const eventTargetCandidates = document.elementsFromPoint(e.clientX, e.clientY);
      const targetWithOptions = this.getTargetWithOptionsForEvent(
        eventTargetCandidates,
        sourceOptions.type,
      );
      const activeTargetId = this.getActiveTargetId(targetWithOptions, sourceOptions);
      this.emitDragEvent('dragStart', dragPayload.sourceId, sourceOptions, activeTargetId);
    },
    onDragMove: (e, dragPayload) => {
      if (!dragPayload.activeDnd) {
        return;
      }
      const sourceOptions = this.dragSources.get(dragPayload.sourceId);
      if (!sourceOptions || !sourceOptions.item) {
        this.dragManager.cancelDrag();
        return;
      }

      this.updatePlaceholderPosition(e, sourceOptions, dragPayload.activeDnd);

      const eventTargetCandidates = document.elementsFromPoint(e.clientX, e.clientY);
      this.updateScroll(e, eventTargetCandidates, dragPayload.activeDnd);

      const targetWithOptions = this.getTargetWithOptionsForEvent(
        eventTargetCandidates,
        sourceOptions.type,
      );
      const activeTargetId = this.getActiveTargetId(targetWithOptions, sourceOptions);
      this.emitDragEvent('dragMove', dragPayload.sourceId, sourceOptions, activeTargetId);
    },
    onDragComplete: (e, dragPayload) => {
      if (!dragPayload.activeDnd) {
        return;
      }
      const sourceOptions = this.dragSources.get(dragPayload.sourceId);
      if (!sourceOptions || !sourceOptions.item) {
        this.dragManager.cancelDrag();
        return;
      }

      sourceOptions.setPlaceholderPosition(undefined);
      window.clearInterval(dragPayload.activeDnd.scroll?.intervalId);

      this.emitter.emit('dragEnd', { id: dragPayload.sourceId }).catch(trackError);

      const eventTargetCandidates = document.elementsFromPoint(e.clientX, e.clientY);
      const targetWithOptions = this.getTargetWithOptionsForEvent(
        eventTargetCandidates,
        sourceOptions.type,
      );
      if (targetWithOptions && targetWithOptions.options.canDrop(sourceOptions.item)) {
        targetWithOptions.options.onDrop(sourceOptions.item);
      }
    },
    onDragCancel: (dragPayload) => {
      if (!dragPayload.activeDnd) {
        return;
      }

      const sourceOptions = this.dragSources.get(dragPayload.sourceId);
      if (sourceOptions) {
        sourceOptions.setPlaceholderPosition(undefined);
      }
      window.clearInterval(dragPayload.activeDnd.scroll?.intervalId);

      this.emitter.emit('dragEnd', { id: dragPayload.sourceId }).catch(trackError);
    },
    dragStartDistancePx: 15,
  };

  private handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.dragManager.isInDragFor(this.dragHandler)) {
      this.dragManager.cancelDrag();
      e.preventDefault();
    }
  };

  private updateScroll(
    e: PointerEvent,
    eventTargetCandidates: Element[],
    activeDnd: {
      scroll?: { el: Element; direction: 'up' | 'down'; intervalId: number } | undefined;
    },
  ) {
    const scrollElement = eventTargetCandidates.find(
      (el) => el.scrollHeight > el.clientHeight && getComputedStyle(el).overflowY !== 'visible',
    );
    if (!scrollElement) {
      window.clearInterval(activeDnd.scroll?.intervalId);
      activeDnd.scroll = undefined;
      return;
    }
    const box = scrollElement.getBoundingClientRect();
    if (e.clientY < box.top + SCROLL_AREA_HEIGHT_PX) {
      if (activeDnd.scroll?.direction !== 'up' || activeDnd.scroll.el !== scrollElement) {
        window.clearInterval(activeDnd.scroll?.intervalId);
        activeDnd.scroll = {
          el: scrollElement,
          direction: 'up',
          intervalId: window.setInterval(() => {
            scrollElement.scrollBy({ top: -SCROLL_PX });
          }, SCROLL_INTERVAL_MS),
        };
      }
    } else if (e.clientY > box.bottom - SCROLL_AREA_HEIGHT_PX) {
      if (activeDnd.scroll?.direction !== 'down' || activeDnd.scroll.el !== scrollElement) {
        window.clearInterval(activeDnd.scroll?.intervalId);
        activeDnd.scroll = {
          el: scrollElement,
          direction: 'down',
          intervalId: window.setInterval(() => {
            scrollElement.scrollBy({ top: SCROLL_PX });
          }, SCROLL_INTERVAL_MS),
        };
      }
    } else {
      window.clearInterval(activeDnd.scroll?.intervalId);
      activeDnd.scroll = undefined;
    }
  }

  private updatePlaceholderPosition(
    e: MouseEvent,
    sourceOptions: DragSourceOptions<typeof LOGINS_DRAG_TYPE>,
    activeDnd: { offsetX: number; offsetY: number; activeTargetId?: DropTargetId | undefined },
  ) {
    const placeholderSize = sourceOptions.getPlaceholderSize();
    sourceOptions.setPlaceholderPosition(
      placeholderSize ?
        {
          // Ensure that the cursor stays inside the drag placeholder even if the placeholder is smaller
          // than the element itself
          x: e.clientX - Math.min(activeDnd.offsetX, placeholderSize.width),
          y: e.clientY - Math.min(activeDnd.offsetY, placeholderSize.height),
        }
      : { x: e.clientX - activeDnd.offsetX, y: e.clientY - activeDnd.offsetY },
    );
  }

  private getActiveTargetId(
    targetWithOptions:
      | { targetId: DropTargetId; options: DropTargetOptions<typeof LOGINS_DRAG_TYPE> }
      | undefined,
    sourceOptions: DragSourceOptions<typeof LOGINS_DRAG_TYPE>,
  ) {
    return targetWithOptions && sourceOptions.type === targetWithOptions.options.accept ?
        targetWithOptions.targetId
      : undefined;
  }

  private emitDragEvent(
    eventName: 'dragStart' | 'dragMove',
    sourceId: string,
    sourceOptions: DragSourceOptions<typeof LOGINS_DRAG_TYPE>,
    activeTargetId: DropTargetId | undefined,
  ) {
    if (!sourceOptions.item) {
      return;
    }
    this.emitter
      .emit(eventName, {
        id: sourceId,
        type: sourceOptions.type,
        item: sourceOptions.item,
        overTargetId: activeTargetId,
      })
      .catch(trackError);
  }

  private getSourceIdForEvent(e: MouseEvent) {
    const eventTargetCandidates = getEventTargetCandidates(e.target);
    for (const [sourceId, options] of this.dragSources.entries()) {
      for (const targetCandidate of eventTargetCandidates) {
        if (targetCandidate === options.el) {
          return sourceId;
        }
      }
    }
    return undefined;
  }

  private getTargetWithOptionsForEvent(eventTargetCandidates: Element[], type: DndType) {
    for (const [targetId, options] of this.dropTargets.entries()) {
      if (options.accept !== type) {
        continue;
      }
      for (const targetCandidate of eventTargetCandidates) {
        if (targetCandidate === options.el) {
          return { targetId, options };
        }
      }
    }
    return undefined;
  }
}

type DndManagerEmitter = Emittery.Typed<{
  dragStart: DndDragEventPayload<DndType>;
  dragMove: DndDragEventPayload<DndType>;
  dragEnd: { id: DragSourceId };
}>;

// The emitter methods are added to DndManager by Emittery.mixin, this is only here for typing
// purposes. We remove 'emit' and 'emitSerial', to prevent accidentally emitting events externally.
interface DndManager extends Omit<DndManagerEmitter, 'emit' | 'emitSerial'> {
  emitter: DndManagerEmitter;
}

export default DndManager;

function getEventTargetCandidates(eventTarget: EventTarget | null) {
  if (!(eventTarget instanceof Element)) {
    return [];
  }
  const result: Element[] = [];
  let cur: Element | null = eventTarget;
  while (cur) {
    result.push(cur);
    cur = cur.parentElement;
  }
  return result;
}

function clamp(min: number, value: number, max: number) {
  return Math.max(min, Math.min(max, value));
}
